import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MentionTextarea } from "@/components/ui/mention-textarea";
import { saveMentions } from "@/lib/mention-utils";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Send, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  read: boolean;
  created_at: string;
  sender?: { username: string | null };
  receiver?: { username: string | null };
}

interface Conversation {
  user_id: string;
  username: string | null;
  last_message: string;
  last_message_time: string;
  unread_count: number;
}

const DashboardMessages = () => {
  const [user, setUser] = useState<any>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [newConversationUserId, setNewConversationUserId] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setUser(user);
        loadConversations(user.id);
        loadAllUsers(user.id);
      }
    });
  }, []);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("messages")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `receiver_id=eq.${user.id}`,
        },
        (payload) => {
          const newMsg = payload.new as Message;
          if (selectedUserId === newMsg.sender_id) {
            setMessages((prev) => [...prev, newMsg]);
            markAsRead(newMsg.id);
          }
          loadConversations(user.id);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `sender_id=eq.${user.id}`,
        },
        (payload) => {
          const newMsg = payload.new as Message;
          if (selectedUserId === newMsg.receiver_id) {
            setMessages((prev) => [...prev, newMsg]);
          }
          loadConversations(user.id);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, selectedUserId]);

  const loadAllUsers = async (currentUserId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("id, username")
      .neq("id", currentUserId);

    if (data) {
      setAllUsers(data);
    }
  };

  const loadConversations = async (userId: string) => {
    const { data: sentMessages } = await supabase
      .from("messages")
      .select("receiver_id, content, created_at, receiver:profiles!messages_receiver_id_fkey(username)")
      .eq("sender_id", userId)
      .order("created_at", { ascending: false });

    const { data: receivedMessages } = await supabase
      .from("messages")
      .select("sender_id, content, created_at, read, sender:profiles!messages_sender_id_fkey(username)")
      .eq("receiver_id", userId)
      .order("created_at", { ascending: false });

    const conversationMap = new Map<string, Conversation>();

    sentMessages?.forEach((msg: any) => {
      const otherId = msg.receiver_id;
      if (!conversationMap.has(otherId)) {
        conversationMap.set(otherId, {
          user_id: otherId,
          username: msg.receiver?.username || "Unknown User",
          last_message: msg.content,
          last_message_time: msg.created_at,
          unread_count: 0,
        });
      }
    });

    receivedMessages?.forEach((msg: any) => {
      const otherId = msg.sender_id;
      const existing = conversationMap.get(otherId);
      if (!existing || new Date(msg.created_at) > new Date(existing.last_message_time)) {
        conversationMap.set(otherId, {
          user_id: otherId,
          username: msg.sender?.username || "Unknown User",
          last_message: msg.content,
          last_message_time: msg.created_at,
          unread_count: existing ? existing.unread_count + (msg.read ? 0 : 1) : (msg.read ? 0 : 1),
        });
      } else if (!msg.read) {
        existing.unread_count++;
      }
    });

    setConversations(Array.from(conversationMap.values()).sort(
      (a, b) => new Date(b.last_message_time).getTime() - new Date(a.last_message_time).getTime()
    ));
  };

  const loadMessages = async (otherUserId: string) => {
    if (!user) return;

    const { data } = await supabase
      .from("messages")
      .select("*")
      .or(`and(sender_id.eq.${user.id},receiver_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},receiver_id.eq.${user.id})`)
      .order("created_at", { ascending: true });

    if (data) {
      setMessages(data as Message[]);
      
      const unreadMessages = data.filter(
        (msg) => msg.receiver_id === user.id && !msg.read
      );
      
      for (const msg of unreadMessages) {
        await markAsRead(msg.id);
      }
    }
  };

  const markAsRead = async (messageId: string) => {
    await supabase
      .from("messages")
      .update({ read: true })
      .eq("id", messageId);
  };

  const sendMessage = async () => {
    if (!user || !selectedUserId || !newMessage.trim()) return;

    const { data, error } = await supabase.from("messages").insert({
      sender_id: user.id,
      receiver_id: selectedUserId,
      content: newMessage.trim(),
    }).select().single();

    if (error) {
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    } else {
      // Save mentions if any
      if (data) {
        await saveMentions(newMessage.trim(), "message", data.id);
      }
      setNewMessage("");
    }
  };

  const startNewConversation = (userId: string) => {
    setSelectedUserId(userId);
    loadMessages(userId);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">Messages</h1>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="ml-auto">
                <MessageSquare className="h-4 w-4 mr-2" />
                New Message
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Start New Conversation</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {allUsers.map((u) => (
                  <Button
                    key={u.id}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => {
                      startNewConversation(u.id);
                      setNewConversationUserId("");
                    }}
                  >
                    <Avatar className="h-8 w-8 mr-3">
                      <AvatarFallback>
                        {(u.username || "U").substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    {u.username || "Unknown User"}
                  </Button>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Card className="md:col-span-1 p-4">
            <h2 className="text-xl font-semibold mb-4">Conversations</h2>
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <Button
                    key={conv.user_id}
                    variant={selectedUserId === conv.user_id ? "secondary" : "ghost"}
                    className="w-full justify-start text-left h-auto py-3"
                    onClick={() => {
                      setSelectedUserId(conv.user_id);
                      loadMessages(conv.user_id);
                    }}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <Avatar>
                        <AvatarFallback>
                          {(conv.username || "U").substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <p className="font-medium truncate">{conv.username || "Unknown User"}</p>
                          {conv.unread_count > 0 && (
                            <span className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{conv.last_message}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(conv.last_message_time).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </ScrollArea>
          </Card>

          <Card className="md:col-span-2 p-4 flex flex-col">
            {selectedUserId ? (
              <>
                <h2 className="text-xl font-semibold mb-4">
                  {conversations.find((c) => c.user_id === selectedUserId)?.username || "Unknown User"}
                </h2>
                <ScrollArea className="flex-1 h-[500px] mb-4">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user?.id ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            msg.sender_id === user?.id
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted"
                          }`}
                        >
                          <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                          <p className="text-xs opacity-70 mt-1">
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <div className="flex gap-2">
                  <MentionTextarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message... (use @ to mention someone)"
                    className="flex-1 resize-none"
                    rows={2}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                  />
                  <Button onClick={sendMessage} disabled={!newMessage.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select a conversation or start a new one</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardMessages;
